import { Controller } from "@hotwired/stimulus";
import { DirectUpload } from "@rails/activestorage";
import { detectSuspicious } from "../utils/plaintextGuard";
import { t } from "../i18n.js";

const SERVER_ERROR_MESSAGES: Record<string, string> = {
  too_large: t('paste_too_large', "Paste too large."),
  invalid_encoding: t('invalid_encoding', "Invalid text encoding (must be UTF-8)."),
  potentially_malicious_content: t('unsafe_content', "Potentially malicious content detected (HTML/JS/data URI)."),
  invalid_text_file: t('invalid_text', "Invalid text."),
};

type SubmitButton = HTMLButtonElement | HTMLInputElement;

export default class extends Controller {
  static targets = ["body", "signedId"];
  static values = { maxSize: Number };

  declare readonly bodyTarget: HTMLTextAreaElement;
  declare readonly signedIdTarget: HTMLInputElement;
  declare readonly hasMaxSizeValue: boolean;
  declare readonly maxSizeValue: number;

  showErrors(msgs: string[], options: { markInvalid?: boolean; disableSubmit?: boolean } = {}) {
    const { markInvalid = true, disableSubmit = true } = options;

    let box = this.element.querySelector("#frontend-pt-errors") as HTMLDivElement | null;
    if (!box) {
      box = document.createElement("div");
      box.id = "frontend-pt-errors";
      box.className = "mb-4 p-3 rounded border border-red-300 bg-red-50 text-red-700 text-sm";
      this.element.prepend(box);
    }
    box.innerHTML = `<strong>${t('fix_before_save', 'Fix the following before saving:')}</strong><ul class="list-disc list-inside mt-1">${msgs
      .map((m) => `<li>${m}</li>`)
      .join("")}</ul>`;

    if (markInvalid) {
      this.bodyTarget.setAttribute("aria-invalid", "true");
      this.bodyTarget.classList.add("border-red-400");
      this.bodyTarget.classList.remove("border-gray-300");
    }

    if (disableSubmit) {
      this.setSubmitDisabled(true);
    }
  }

  clearErrors() {
    const box = this.element.querySelector("#frontend-pt-errors");
    if (box) box.remove();
    this.bodyTarget.removeAttribute("aria-invalid");
    this.bodyTarget.classList.remove("border-red-400");
    this.bodyTarget.classList.add("border-gray-300");
    this.setSubmitDisabled(false);
  }

  validateClient(text: string): string[] {
    const errs: string[] = [];
    if (!text) return errs;

    if (text.includes("\u0000")) {
      errs.push(t('invalid_text', "Invalid text."));
      return errs;
    }

    const max = this.hasMaxSizeValue ? this.maxSizeValue : 1_000_000;
    const size = new Blob([text]).size;
    if (size > max) {
      errs.push(t('paste_too_large', "Paste too large."));
    }

    const findings = detectSuspicious(text);
    if (findings.length) {
      errs.push(t('unsafe_content', "Potentially malicious content detected (HTML/JS/data URI)."));
    }

    return errs;
  }

  input(event: Event) {
    const t = (event.target as HTMLTextAreaElement).value || "";
    this.clearErrors();
    const errs = this.validateClient(t);
    if (errs.length) this.showErrors(errs);
  }

  submit(event: Event) {
    // The paste field is disabled whenever "Upload file" is the active
    // source (see document_source_controller) -- when disabled, this is not
    // the source being submitted, so skip paste validation/upload entirely.
    if (this.bodyTarget.disabled) return;

    const text = this.bodyTarget.value || "";
    this.clearErrors();

    const errs = this.validateClient(text);
    if (errs.length) {
      event.preventDefault();
      this.showErrors(errs);
      return;
    }

    if (!text || this.signedIdTarget.value) return;

    event.preventDefault();
    this.setSubmitDisabled(true);

    const file = new File([text], "paste.txt", { type: "text/plain" });
    const upload = new DirectUpload(file, "/rails/active_storage/direct_uploads");

    upload.create((error, blob) => {
      if (error) {
        const message = this.messageForServerError(error);
        if (message) {
          this.showErrors([message]);
        } else {
          this.setSubmitDisabled(false);
          this.showErrors([t('upload_failed', "Upload failed. Please try again.")], { markInvalid: false, disableSubmit: false });
        }
        return;
      }

      const signedId = (blob as { signed_id?: string } | null)?.signed_id;
      if (signedId) {
        this.signedIdTarget.value = signedId;
      }
      this.bodyTarget.value = "";
      this.element.submit();
    });
  }

  private setSubmitDisabled(disabled: boolean) {
    const buttons = this.submitButtons();
    buttons.forEach((btn) => {
      btn.disabled = disabled;
      if (disabled) {
        btn.setAttribute("aria-disabled", "true");
      } else {
        btn.removeAttribute("aria-disabled");
      }
    });
  }

  private submitButtons(): SubmitButton[] {
    return Array.from(
      this.element.querySelectorAll<SubmitButton>("button[type='submit'], input[type='submit']")
    );
  }

  private messageForServerError(error: unknown): string | null {
    if (typeof error === "string") {
      return SERVER_ERROR_MESSAGES[error] || null;
    }
    return null;
  }
}
